import { AfterContentInit, Component, Injector, Input, OnInit, TemplateRef, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { LegendPosition } from '@swimlane/ngx-charts';
import { BaseRequest, FormatTemplateMessage, HeaderComponentObject, TemplateMessageObject, TemplateMessageRequest } from '@trendbuild/trend-cloud-api';
import { ButtonActionsTypeEnum, TrendZAPIService } from '@trendbuild/trend-z-api';
import { AbstractComponent, Channel, ChannelService, ChannelTypeEnum, ConfirmationComponent, ContactListModalComponent, ContactService, MessageHelper, MetadataMessage } from 'lib-trend-core';
import { BehaviorSubject, concatMap, finalize, from, map, mergeMap, Observable, of, takeUntil, tap, timer } from 'rxjs';
import { ChannelEditComponent } from '../channel-edit/channel-edit.component';
import { buildMetaOAuthUrl, MetaOAuthChannelType } from '../meta-oauth.util';

@Component({
    selector: 'channel-config-component',
    // templateUrl: './channel-config.component.html',
    templateUrl: './channel-config.v2.component.html',
    styleUrls: ['./channel-config.component.scss', './channel-config.v2.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChannelConfigComponent extends AbstractComponent implements OnInit, AfterContentInit {

  @Input('channel') channel: Channel;

  private trendZAPIService: TrendZAPIService = new TrendZAPIService();

  @ViewChild('testChannelModal') testChannelModal: TemplateRef<any>;
  testDialogRef: any;

  private loadingSpinnerSubject = new BehaviorSubject<boolean>(false);
  loadingSpinner$ = this.loadingSpinnerSubject.asObservable();

  INSTANCE_CODE_DATA: string;
  countContacts: number = 0;
  countChats: number = 0;
  countMessages: number = 0;

  dataChartOfChannel: Array<{ name: string, series: Array<{ name: string, value: number }> }>;
  totalContacts: string;
  totalMessages: string;
  totalAttendances: string;

  // VoIP (VOIP_API) — call counters. Backend source not available yet (no call logging in scope); defaults to 0.
  totalCallsMade: string = '0';
  totalCallsReceived: string = '0';

  selectedTemplate: TemplateMessageObject;
  showPreviewTemplate: boolean = false;
  templateMessage: MetadataMessage;

  legendPosition: LegendPosition = LegendPosition.Right;
  customColors: Array<{ name: string, value: string }> = [
    { name: 'Mensagens enviadas', value: '#4213F6' },
    { name: 'Mensagens recebidas', value: '#8F8CFF' },
  ];

  variables: Record<string, string> = {};

  checkedRejectCallAuto = false;
  isLoadingRejectCallAuto = false;

  cloudAPI: ChannelTypeEnum = ChannelTypeEnum.CLOUD_API;
  zAPI: ChannelTypeEnum = ChannelTypeEnum.Z_API;
  instagramAPI: ChannelTypeEnum = ChannelTypeEnum.INSTAGRAM_API;
  messengerAPI: ChannelTypeEnum = ChannelTypeEnum.MESSENGER_API;
  rcsAPI: ChannelTypeEnum = ChannelTypeEnum.RCS_API;
  voipAPI: ChannelTypeEnum = ChannelTypeEnum.VOIP_API;
  webchatAPI: ChannelTypeEnum = ChannelTypeEnum.WEBCHAT_API;

  constructor(
    injector: Injector,
    public channelService: ChannelService,
    private dialog: MatDialog,
    private contactService: ContactService,
    private dialogContact: MatDialog,
    private fb: FormBuilder,
  ) {
    super(injector);
  }

  ngOnInit() {
    this.getChannelWithMetrics();
  }

  ngAfterContentInit(): void {
    this.init();
  }

  // Method for fetch base64 and with interval of 20 seconds
  init(): void {
    if (!!this.channel && this.channel.type !== ChannelTypeEnum.CLOUD_API && this.channel.type !== ChannelTypeEnum.VOIP_API && this.channel.type !== ChannelTypeEnum.WEBCHAT_API) {
      this.loadingSpinnerSubject.next(true);
      timer(0, 30000).pipe(
        mergeMap(() => this.channelService.syncInstanceByChannel(this.channel._id)),
        concatMap((channel) => {
          this.channel = channel;
          if (channel.type === ChannelTypeEnum.Z_API) {
            this.getInfoZAPIChannel();
          }
          if (this.channel.connected) {
            return of(null);
          } else {
            return this.channelService.getInstanceConnect(this.channel._id)
              .pipe(
                map((result) => {
                  if (channel.type === ChannelTypeEnum.Z_API) {
                    this.INSTANCE_CODE_DATA = result['value'];
                  }
                }),
                takeUntil(this.destroy$),
              );
          }
        }),
        tap(() => this.loadingSpinnerSubject.next(false)),
        takeUntil(this.destroy$),
      ).subscribe({
        complete: () => this.loadingSpinnerSubject.next(false)
      });
    }
  }

  sync(): void {
    this.loading = true;
    this.loadingSpinnerSubject.next(true);
    this.channelService.syncInstanceByChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel: Channel) => this.channel = channel,
        error: (err) => this.alertService.error(err.error.message),
        complete: (() => [this.loadingSpinnerSubject.next(false), this.loading = false])
      });
  }

  syncContacts(): void {
    this.loading = true;
    this.loadingSpinnerSubject.next(true);
    this.channelService.syncContactsByChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel: Channel) => {
          this.channel = channel;
          this.alertService.success(this.t('channel.channelConfig.contactsSyncedSuccess'));
        },
        error: (err) => this.alertService.error(err.error.message),
        complete: (() => [this.loadingSpinnerSubject.next(false), this.loading = false])
      });
  }

  testChannel(): void {
    this.loading = false;
    this.showPreviewTemplate = false;
    this.selectedTemplate = null;
    this.templateMessage = null;

    const formGroupConfig = {
      phoneNumber: ['', [Validators.required]],
      messagem: ['', [Validators.required]]
    };

    if (this.channel.type === ChannelTypeEnum.Z_API) {
      Object.assign(formGroupConfig, {
        messageType: ['simple'],
        title: [''],
        footer: [''],
        buttonActions: this.fb.array([])
      });
    }

    this.formGroup = this.fb.group(formGroupConfig);

    this.testDialogRef = this.dialog.open(this.testChannelModal, {
      width: '1050px'
    });

    this.testDialogRef.afterClosed().subscribe(result => {
      if (result === false) {
        this.loading = false;
      }
    });
  }

  sendTestMessage(): void {
    if (this.formGroup.invalid && !this.selectedTemplate) return;

    const { phoneNumber, messagem } = this.formGroup.value;
    this.loading = true;

    let sendMessage$: Observable<any>;

    if (this.showPreviewTemplate && this.selectedTemplate) {
      sendMessage$ = this.getSendMessageObservable(phoneNumber, this.selectedTemplate, null);
    } else {
      sendMessage$ = this.getSendMessageObservable(phoneNumber, null, messagem);
    }

    if (!sendMessage$) {
      this.alertService.error(this.t('channel.channelConfig.channelNotSupported'));
      this.loading = false;
      return;
    }

    sendMessage$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.alertService.success(this.t('channel.channelConfig.messageSentSuccess'));
          this.testDialogRef.close(true);
        },
        error: (err) => {
          this.alertService.error(err.error?.message || this.t('channel.channelConfig.testMessageError'));
        },
        complete: () => {
          this.loading = false;
        }
      });
  }

  private getSendMessageObservable(phoneNumber: string, selectedTemplate?: TemplateMessageObject, messagem?: string): Observable<any> | null {
    switch (this.channel.type) {
      case ChannelTypeEnum.CLOUD_API:
        // Credentials are resolved server-side from the channel — they never leave the API.
        const templateMessage: Omit<TemplateMessageRequest, keyof BaseRequest> = {
          to: phoneNumber,
          template: selectedTemplate.name,
          language: selectedTemplate.language,
          bodyParams: this.templateMessage?.bodyParams ?? [],
          headerParams: this.templateMessage?.headerParams ?? [],
        };

        const headerComponent = <HeaderComponentObject>this.selectedTemplate.components.find(c => c.type === 'HEADER');
        if (['IMAGE', 'DOCUMENT', 'VIDEO'].includes(headerComponent?.format)) {
          const format: FormatTemplateMessage = <FormatTemplateMessage>headerComponent.format.toLowerCase();
          templateMessage.headerParams = this.templateMessage.headerParams;
          templateMessage.format = format;
        }
        // The category goes beside the payload, not inside it: on a credit-line channel it is what
        // the test send is charged under, and without it the movement is recorded unpriced
        // (SPEC-2026-10-whatsapp-wallet-credit-line-channels RF-11). Sent for every Cloud API
        // channel — the server decides which channels consume from the wallet, not this screen.
        return this.channelService.sendTemplateMessageByChannel(this.channel._id, templateMessage, selectedTemplate.category);

      default:
        return null;
    }
  }

  onSelectTemplate(template: TemplateMessageObject) {
    this.buildVariables();
    this.selectedTemplate = template;
    this.showPreviewTemplate = true;
  }

  changeTemplate() {
    this.showPreviewTemplate = false;
    this.templateMessage = undefined;
  }

  setTemplateMessage(templateMessage: MetadataMessage) {
    this.templateMessage = templateMessage;
    this.formGroup.get('messagem').setValue(templateMessage.previewText);
  }

  restart(): void {
    this.loading = true;
    this.loadingSpinnerSubject.next(true);
    this.channelService.restartInstanceByChannel(this.channel._id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (channel: Channel) => this.channel = channel,
        error: (err) => this.alertService.error(err.error.message),
        complete: (() => this.loadingSpinnerSubject.next(false))
      });
  }

  /**
   * Disconnects the channel without deleting it: the connection data (tokens) is cleared and the
   * channel — with every conversation recorded on it — stays. For Instagram/Messenger this is the
   * way out of an expired token, since Meta cannot renew one that already expired: disconnect, then
   * reconnect through a new authorization.
   *
   * `loading` is only raised after the confirmation: raising it before left every button on the page
   * disabled when the user cancelled the dialog.
   */
  disconnect(): void {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
      data: this.isReconnectableChannel() ? this.t('channel.channelConfig.disconnectMetaConfirm') : undefined,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.loading = true;
        this.loadingSpinnerSubject.next(true);
        this.channelService.disconnectInstanceByChannel(this.channel._id).subscribe({
          next: (channel: Channel) => {
            this.channel = channel;
            this.alertService.success(this.t('channel.channelConfig.disconnectSuccess'));
          },
          error: (err) => {
            this.alertService.error(err.error?.message ?? this.t('channel.channelConfig.disconnectError'));
            this.loadingSpinnerSubject.next(false);
            this.loading = false;
          },
          complete: (() => [this.loadingSpinnerSubject.next(false), this.loading = false])
        });
      }
    });
  }

  /** Instagram and Messenger — the channels connected through a Facebook Login authorization. */
  isMetaChannel(): boolean {
    return this.channel?.type === ChannelTypeEnum.INSTAGRAM_API || this.channel?.type === ChannelTypeEnum.MESSENGER_API;
  }

  /** WhatsApp Cloud API — connected through the Embedded Signup, not a Facebook Login redirect. */
  isCloudAPIChannel(): boolean {
    return this.channel?.type === ChannelTypeEnum.CLOUD_API;
  }

  /**
   * The channels a broken connection can be fixed on without deleting them: their token is cleared
   * on disconnect and refreshed on a new authorization, so the conversation history stays.
   *
   * The authorization differs — Cloud API opens the Embedded Signup, Instagram/Messenger redirect to
   * Facebook Login — but the disconnect, the warning and the promise to the customer are the same.
   */
  isReconnectableChannel(): boolean {
    return this.isMetaChannel() || this.isCloudAPIChannel();
  }

  /**
   * The channel came back from the Embedded Signup (connect or reconnect). `init()` cannot do this
   * job: it deliberately skips Cloud API channels, since they have no instance to poll.
   */
  onEmbeddedConnected(channel: Channel): void {
    this.channel = channel;
    this.getChannelWithMetrics();
  }

  /**
   * Reopens the Meta authorization for this channel. Carrying the channel id in the OAuth `state`
   * makes the redirect page refresh this channel's connection data instead of connecting every page
   * the user administers — the channel, and its whole history, is kept.
   */
  reconnect(): void {
    if (!this.isMetaChannel()) {
      return;
    }
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
      data: this.t('channel.channelConfig.reconnectConfirm'),
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        window.location.href = buildMetaOAuthUrl(<MetaOAuthChannelType>this.channel.type, this.channel._id);
      }
    });
  }

  private getChannelWithMetrics(): void {
    this.loadingContent = true;
    this.channelService.getByIdAndMetrics(this.channel._id, this.channel.identifier)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (value) => {
          this.dataChartOfChannel = [
            {
              name: 'Mensagens enviadas',
              series: [
                {
                  name: 'Mensagens enviadas',
                  value: value.find((value) => value).sentMessages ?? 0,
                },
              ],
            },
            {
              name: 'Mensagens recebidas',
              series: [
                {
                  name: 'Mensagens recebidas',
                  value: value.find((value) => value).receivedMessages ?? 0,
                },
              ],
            },
          ];

          this.totalContacts = value.find((value) => value).totalContacts.toString() ?? '0';
          this.totalMessages = value.find((value) => value).totalMessages.toString() ?? '0';
          this.totalAttendances = value.find((value) => value).totalAttendance.toString() ?? '0';
          this.loadingContent = false;
        },
        error: (err) => this.alertService.error(err.error.message),
      });
  }

  edit(item: Channel): void {
    const dialogRef = this.dialog.open(ChannelEditComponent, {
      width: '600px',
      data: item
    });

    dialogRef.afterClosed().subscribe(result => {
    });
  }

  onDelete(channel: Channel) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.channelService.delete(channel._id).subscribe({
          next: () => {
            this.alertService.success();
            this.router.navigate(['/channel']);
          },
          error: err => this.alertService.error(err.error.message)
        })
      }
    });
  }

  blockOrUnblockContact(): void {
    const dialogRef = this.dialogContact.open(ContactListModalComponent, {
      maxHeight: '1200px',
      minHeight: '900px',
      width: '1200px',
      data: { channel: this.channel, block: true },
    });
    dialogRef.afterClosed().subscribe((value) => {
      if (!!value) {
        if (value.block) {
          this.contactService.blockContact(value.contact, this.channel).subscribe({
            next: () => this.alertService.success(this.t('channel.channelConfig.contactBlockedSuccess')),
          });
        } else {
          this.contactService.unblockContact(value.contact, this.channel).subscribe({
            next: () => this.alertService.success(this.t('channel.channelConfig.contactUnblockedSuccess')),
          });
        }
      }
    });
  }

  private buildVariables(): void {
    this.variables = {
      'Saudação': MessageHelper.getGreeting(),
      'Nome do Canal': this.channel.name,
      'Número do Canal': this.channel.identifier,
      'Nome do Atendente': super.getNameCurrentUser(),
    };
  }

  get buttonActionsControls() {
    return this.formGroup.get('buttonActions') as any;
  }

  addButton() {
    if (!this.buttonActionsControls || this.buttonActionsControls.length >= 3) return;

    const buttonGroup = this.fb.group({
      type: [ButtonActionsTypeEnum.reply],
      label: ['', Validators.required],
      phone: [''],
      url: ['']
    });

    this.buttonActionsControls.push(buttonGroup);
  }

  removeButton(index: number) {
    if (!this.buttonActionsControls) return;
    this.buttonActionsControls.removeAt(index);
  }

  get ButtonActionsTypeEnum() {
    return ButtonActionsTypeEnum;
  }

  // Deactivate in V2
  handleRejectCallAuto(event: MatSlideToggleChange): void {
    const request = {
      id: this.channel.credentials.id,
      token: this.channel.credentials.token,
      value: event.checked,
    };

    this.isLoadingRejectCallAuto = true;

    from(this.trendZAPIService.updateCallRejectAuto(request))
      .pipe(takeUntil(this.destroy$), finalize(() => this.isLoadingRejectCallAuto = false))
      .subscribe({
        next: () => {
          this.alertService.success(this.t('channel.channelConfig.settingUpdatedSuccess'));
          this.checkedRejectCallAuto = request.value;
        },
        error: (err) => {
          const message = err?.error?.message || this.t('channel.channelConfig.settingUpdateError');
          this.alertService.error(message);
          this.checkedRejectCallAuto = !request.value;
        },
      });
  }

  // Activate in V2
  // handleRejectCallAuto(event: Event): void {
  //   const request = {
  //     id: this.channel.credentials.id,
  //     token: this.channel.credentials.token,
  //     value: (event.target as HTMLInputElement).checked,
  //   };

  //   this.isLoadingRejectCallAuto = true;

  //   from(this.trendZAPIService.updateCallRejectAuto(request))
  //     .pipe(takeUntil(this.destroy$), finalize(() => this.isLoadingRejectCallAuto = false))
  //     .subscribe({
  //       next: () => {
  //         this.alertService.success('Configuração atualizada com sucesso.');
  //         this.checkedRejectCallAuto = request.value;
  //       },
  //       error: (err) => {
  //         const message = err?.error?.message || 'Erro ao atualizar configuração.';
  //         this.alertService.error(message);
  //         this.checkedRejectCallAuto = !request.value;
  //       },
  //     });
  // }


  getInfoZAPIChannel(): void {
    from(this.trendZAPIService.getME({ id: this.channel.credentials.id, token: this.channel.credentials.token }))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.checkedRejectCallAuto = response.callRejectAuto;
        },
        error: (err) => {
          const message = err?.error?.message || this.t('channel.channelConfig.channelInfoError');
          this.alertService.error(message);
        },
      });
  }

}
